// Persona title generation based on interests
export const generatePersonaTitle = (passions: string[]): string => {
  const personaMappings: Record<string, string[]> = {
    Festivals: ["Festival", "Explorer"],
    Travel: ["Travel", "Adventurer"],
    "Plant-based": ["Green", "Enthusiast"],
    Movies: ["Cinema", "Buff"],
    "Road Trips": ["Road", "Warrior"],
    Books: ["Book", "Lover"],
    Writer: ["Creative", "Writer"],
    Fashion: ["Style", "Maven"],
    "Stand up Comedy": ["Comedy", "Fan"],
    Coffee: ["Coffee", "Connoisseur"],
    Chatting: ["Social", "Butterfly"],
    Cycling: ["Cycling", "Enthusiast"],
    DIY: ["DIY", "Creator"],
    "Dog lover": ["Dog", "Lover"],
    "House Parties": ["Party", "Host"],
    Swimming: ["Water", "Explorer"],
    Athlete: ["Fitness", "Athlete"],
    Bollywood: ["Bollywood", "Fan"],
    Hollywood: ["Movie", "Buff"],
    Cricket: ["Sports", "Fan"],
    Trivia: ["Trivia", "Master"],
    "Tea Break": ["Tea", "Lover"],
    Environmentalism: ["Eco", "Warrior"],
    Yoga: ["Zen", "Seeker"],
    Running: ["Running", "Enthusiast"],
    Music: ["Music", "Lover"],
    Astrology: ["Star", "Gazer"],
    Spirituality: ["Spiritual", "Seeker"],
    Art: ["Art", "Enthusiast"],
    Photography: ["Photo", "Explorer"],
    Baking: ["Baking", "Artist"],
    Volunteering: ["Community", "Helper"],
    Podcasts: ["Podcast", "Addict"],
    Startups: ["Tech", "Innovator"],
    History: ["History", "Buff"],
    Politics: ["Political", "Thinker"],
  };

  if (passions.length === 0) return "Curious Explorer";

  // Get all passions that have mappings
  const mappedPassions = passions.filter((passion) => personaMappings[passion]);

  if (mappedPassions.length > 0) {
    // Randomly pick one of the mapped passions
    const randomPassion =
      mappedPassions[Math.floor(Math.random() * mappedPassions.length)];
    const [adjective, noun] = personaMappings[randomPassion];
    return `${adjective} ${noun}`;
  }

  // Fallback titles based on passion categories - also randomized
  const fallbackOptions: string[] = [];

  if (
    passions.some((p) => ["Festivals", "Movies", "Music", "Art"].includes(p))
  ) {
    fallbackOptions.push("Creative Soul", "Art Enthusiast", "Culture Lover");
  }
  if (passions.some((p) => ["Travel", "Road Trips", "Outdoor"].includes(p))) {
    fallbackOptions.push("Adventure Seeker", "World Explorer", "Journey Lover");
  }
  if (
    passions.some((p) =>
      ["Athlete", "Swimming", "Running", "Cycling", "Yoga"].includes(p),
    )
  ) {
    fallbackOptions.push(
      "Fitness Enthusiast",
      "Active Explorer",
      "Wellness Seeker",
    );
  }
  if (
    passions.some((p) =>
      ["Chatting", "House Parties", "Stand up Comedy"].includes(p),
    )
  ) {
    fallbackOptions.push(
      "Social Connector",
      "People Person",
      "Community Builder",
    );
  }

  // Randomly pick from available fallback options
  if (fallbackOptions.length > 0) {
    return fallbackOptions[Math.floor(Math.random() * fallbackOptions.length)];
  }

  return "Curious Explorer";
};

// Generate multiple persona title alternatives
export const generateAlternativePersonas = (
  passions: string[],
  currentPersona?: string,
): string[] => {
  const alternatives: string[] = [];

  // Get primary recommendation
  const primary = generatePersonaTitle(passions);
  if (primary !== currentPersona) {
    alternatives.push(primary);
  }

  // Generate category-based alternatives
  if (
    passions.some((p) => ["Festivals", "Movies", "Music", "Art"].includes(p))
  ) {
    const creativeOptions = [
      "Creative Soul",
      "Art Enthusiast",
      "Culture Lover",
    ];
    alternatives.push(
      ...creativeOptions.filter((option) => option !== currentPersona),
    );
  }

  if (passions.some((p) => ["Travel", "Road Trips"].includes(p))) {
    const travelOptions = [
      "Adventure Seeker",
      "World Explorer",
      "Journey Lover",
    ];
    alternatives.push(
      ...travelOptions.filter((option) => option !== currentPersona),
    );
  }

  if (
    passions.some((p) =>
      ["Athlete", "Swimming", "Running", "Cycling", "Yoga"].includes(p),
    )
  ) {
    const fitnessOptions = [
      "Fitness Enthusiast",
      "Active Explorer",
      "Wellness Seeker",
    ];
    alternatives.push(
      ...fitnessOptions.filter((option) => option !== currentPersona),
    );
  }

  if (
    passions.some((p) =>
      ["Chatting", "House Parties", "Stand up Comedy"].includes(p),
    )
  ) {
    const socialOptions = [
      "Social Connector",
      "People Person",
      "Community Builder",
    ];
    alternatives.push(
      ...socialOptions.filter((option) => option !== currentPersona),
    );
  }

  // Remove duplicates and limit to 3 alternatives
  return [...new Set(alternatives)].slice(0, 3);
};
